import { cashAuditLogs, currencySymbols, type CashAuditLog } from '@/mocks/cash';

interface AuditLogPanelProps {
  onClose: () => void;
}

export default function AuditLogPanel({ onClose }: AuditLogPanelProps) {
  const logs = [...cashAuditLogs];

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center p-4">
      <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose} />
      <div
        className="relative w-full max-w-2xl flex flex-col rounded-spa border overflow-hidden animate-scale-in max-h-[85vh]"
        style={{ background: 'var(--card-bg)', borderColor: 'var(--card-border)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0" style={{ borderColor: 'var(--border-color)' }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full" style={{ background: 'rgba(201,169,98,0.12)' }}>
              <i className="ri-history-line text-sm" style={{ color: '#C9A962' }} />
            </div>
            <div>
              <h3 className="text-base font-semibold font-display" style={{ color: 'var(--text-primary)' }}>
                İşlem Audit Logu
              </h3>
              <p className="text-[10px]" style={{ color: 'var(--text-muted)' }}>Düzenleme ve silme geçmişi</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
          >
            <i className="ri-close-line" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto min-h-0">
          {logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 space-y-3">
              <div className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: 'var(--bg-tertiary)' }}>
                <i className="ri-shield-check-line text-xl" style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Henüz log kaydı bulunmuyor</p>
            </div>
          ) : (
            <div className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
              {logs.map((log: CashAuditLog) => {
                const isDelete = log.action === 'delete';
                const tx = log.txSnapshot;
                const amountStr = `${currencySymbols[tx.currency || 'TRY']}${tx.amount.toLocaleString('tr-TR')}`;
                const accountLabel = tx.account === 'cash' ? 'Nakit Kasa' : 'Banka Hesabı';
                return (
                  <div key={log.id} className="p-4 hover:bg-theme-hover transition-colors">
                    <div className="flex items-start gap-3">
                      <div
                        className="w-8 h-8 flex items-center justify-center rounded-full flex-shrink-0 mt-0.5"
                        style={{ background: isDelete ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)' }}
                      >
                        <i
                          className={isDelete ? 'ri-delete-bin-line text-xs' : 'ri-edit-line text-xs'}
                          style={{ color: isDelete ? '#EF4444' : '#3B82F6' }}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span
                            className="text-xs font-semibold px-2 py-0.5 rounded-full"
                            style={{
                              background: isDelete ? 'rgba(239,68,68,0.12)' : 'rgba(59,130,246,0.12)',
                              color: isDelete ? '#EF4444' : '#3B82F6',
                            }}
                          >
                            {isDelete ? 'SİLİNDİ' : 'DÜZENLENDİ'}
                          </span>
                          <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                            {tx.description}
                          </span>
                          {tx.customerName && (
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>— {tx.customerName}</span>
                          )}
                        </div>
                        <div className="mt-1.5 flex items-center gap-3 flex-wrap">
                          <span className="text-xs" style={{ color: tx.type === 'income' ? '#10B981' : '#EF4444' }}>
                            {tx.type === 'income' ? '+' : '-'}{amountStr}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {accountLabel}{tx.bankName ? ` · ${tx.bankName}` : ''}
                          </span>
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                            {tx.paymentType}
                          </span>
                        </div>
                        {log.reason && (
                          <div className="mt-1.5 px-2 py-1 rounded text-xs inline-block" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>
                            <i className="ri-chat-3-line mr-1" />
                            {log.reason}
                          </div>
                        )}
                        {isDelete && (
                          <div className="mt-2 p-2 rounded text-[10px] font-mono leading-4 whitespace-pre-wrap" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-muted)' }}>
                            {`Tarih/Saat: ${tx.date} ${tx.time}\nİşlemi Yapan: ${log.performedBy}\nLog Zamanı: ${formatTime(log.timestamp)}`}
                          </div>
                        )}
                        {!isDelete && log.previousSnapshot && (
                          <div className="mt-2 grid grid-cols-2 gap-2">
                            <div className="p-2 rounded text-[10px]" style={{ background: 'rgba(239,68,68,0.06)', color: '#EF4444' }}>
                              <p className="font-semibold mb-1">Eski</p>
                              <p>{log.previousSnapshot.paymentType} · {currencySymbols[log.previousSnapshot.currency || 'TRY']}{log.previousSnapshot.amount.toLocaleString('tr-TR')}</p>
                            </div>
                            <div className="p-2 rounded text-[10px]" style={{ background: 'rgba(16,185,129,0.06)', color: '#10B981' }}>
                              <p className="font-semibold mb-1">Yeni</p>
                              <p>{tx.paymentType} · {amountStr}</p>
                            </div>
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] whitespace-nowrap" style={{ color: 'var(--text-muted)' }}>
                        {formatTime(log.timestamp)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}